import { InnerBox } from "@/components/elements/InnerBox";
import { JobListTop } from "@/features/job/components/job-list/JobListTop";
import { usePagination } from "@/hooks/usePagination";
import { divider } from "@/theme/colors";
import { Stack, Typography } from "@mui/material";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { TGetJobListReqDto } from "@/api/services/main/job/dtos/get-job-list.dto";
import { useJobList } from "@/api/services/main/job";
import { Pagination } from "@/components/elements/Pagination";
import { JobListTable } from "@/features/job/components/job-list/JobListTable";

type TJobListPageProps = {};

type TJobFilterForm = {
  keyword: string;
  companySpecificIndustryId: string;
  caseStatus: string;
};

const defaultValues: TJobFilterForm = {
  keyword: "",
  companySpecificIndustryId: "",
  caseStatus: "",
};

export const JobListPage: FC<TJobListPageProps> = () => {
  const pagReq = usePagination();
  const navigate = useNavigate();

  const [formValues, setFormValues] = useState<TJobFilterForm>({ ...defaultValues });
  const [filters, setFilters] = useState<TJobFilterForm>({ ...defaultValues });
  const [searchParams, _setSearchParams] = useSearchParams();

  const query: TGetJobListReqDto = {
    page: pagReq.page || START_PAGE,
    perPage: pagReq.perPage || DEFAULT_PER_PAGE,
    ...filters,
  };

  if (searchParams.get("sorts")) {
    query.sorts = searchParams.get("sorts") ? [searchParams.get("sorts") as `${string}|${string}`] : [];
  }

  const { data, isLoading } = useJobList(query);

  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    const allEmpty =
      !formValues.companySpecificIndustryId && !formValues.keyword && !formValues.caseStatus;

    if (allEmpty) {
      setFilters(defaultValues);
    }
  }, [formValues]);

  const navigateFirstPage = () => {
    const params = new URLSearchParams(location.search);
    params.set("page", "1");
    navigate(`/job?${params.toString()}`);
  };

  const onSubmit = (vals: TJobFilterForm) => {
    setFilters(vals);
    navigateFirstPage();
  };

  const handleUpdateFormValues = (key: keyof TJobFilterForm, value: unknown) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

   const handleClearFilter = () => {
    setFormValues(defaultValues)
    setFilters(defaultValues);
    navigateFirstPage();
  };

  return (
    <Stack minHeight="100%">
      <Stack mb={5} justifyContent="space-between" alignItems="center" direction="row" width="100%">
        <Typography variant="head24Bold">求人管理</Typography>
      </Stack>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack>
          {/* Search and Filter Section */}
          <JobListTop
            formValues={formValues}
            onChangeFormValues={handleUpdateFormValues}
            onSubmit={(vals) => onSubmit(vals)}
            handleClearFilter = {handleClearFilter}
            pagination={data?.pagination}
          />
          <Stack spacing={3}>
            <JobListTable rows={data?.data ?? []} isLoading={isLoading} />
            <Pagination totalPage={data?.pagination?.totalPage ?? 1} />
          </Stack>
        </Stack>
      </InnerBox>
    </Stack>
  );
};
